import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export function GuardLayout({
  children,
  currentPage,
}: {
  children: React.ReactNode;
  currentPage: string;
}) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const navItems = [
    {
      label: 'Dashboard',
      path: '/guard/dashboard',
      icon: 'ri-dashboard-3-line',
    },
    {
      label: 'Outward Passes',
      path: '/guard/outward',
      icon: 'ri-logout-box-r-line',
    },
    {
      label: 'Inward Passes',
      path: '/guard/inward',
      icon: 'ri-login-box-line',
    },
  ];

  return (
    <div className="flex h-screen bg-[#f8fafc] overflow-hidden">
      {/* Sidebar */}
      <aside className={`bg-[#1e293b] text-white flex flex-col shrink-0 transition-all duration-300 ${isSidebarOpen ? 'w-56' : 'w-16'}`}>
        {/* Logo */}
        <div className={`flex items-center h-[60px] border-b border-white/10 shrink-0 ${isSidebarOpen ? 'px-3 gap-3' : 'justify-center'}`}>
          {isSidebarOpen && (
            <>
              <div className="w-8 h-8 bg-[#4f46e5] rounded-lg flex items-center justify-center shrink-0">
                <i className="ri-shield-user-fill text-white text-sm" />
              </div>
              <div className="flex-1 overflow-hidden whitespace-nowrap">
                <p className="text-sm font-bold text-white leading-tight">
                  Guard Portal
                </p>
                <p className="text-[10px] text-slate-400">InvenPro Security</p>
              </div>
            </>
          )}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="w-8 h-8 rounded text-slate-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer shrink-0 flex items-center justify-center"
            title="Toggle Sidebar"
          >
            <i className={isSidebarOpen ? 'ri-menu-fold-line text-lg' : 'ri-menu-unfold-line text-lg'} />
          </button>
        </div>
        
        {/* Nav */}
        <nav className="flex-1 py-4 px-2 space-y-0.5">
          {isSidebarOpen && (
            <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500 whitespace-nowrap">
              Gate Pass
            </p>
          )}
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center rounded-lg transition-all cursor-pointer ${
                isSidebarOpen ? 'gap-3 px-3 py-2.5 text-left' : 'justify-center py-2.5'
              } ${
                currentPage === item.path
                  ? 'bg-[#4f46e5] text-white'
                  : 'text-slate-300 hover:bg-white/10 hover:text-white'
              }`}
              title={!isSidebarOpen ? item.label : undefined}
            >
              <i className={`${item.icon} text-lg leading-none shrink-0`} />
              {isSidebarOpen && (
                <span className="text-sm font-medium whitespace-nowrap">
                  {item.label}
                </span>
              )}
            </button>
          ))}
        </nav>
        
        {/* User */}
        <div className="border-t border-white/10 p-3 shrink-0">
          {isSidebarOpen ? (
            <div className="flex items-center gap-2 px-2 py-2 mb-1">
              <div className="w-8 h-8 rounded-full bg-[#4f46e5] flex items-center justify-center text-xs font-bold text-white shrink-0">
                {(user?.name ?? 'SG')
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase()}
              </div>
              <div className="min-w-0 flex-1 overflow-hidden">
                <p className="text-sm font-medium text-white truncate">
                  {user?.name ?? 'Guard'}
                </p>
                <p className="text-[11px] text-slate-400 whitespace-nowrap">Security Guard</p>
              </div>
            </div>
          ) : (
            <div className="flex justify-center mb-2">
              <div className="w-8 h-8 rounded-full bg-[#4f46e5] flex items-center justify-center text-xs font-bold text-white shrink-0" title={user?.name ?? 'Guard'}>
                {(user?.name ?? 'SG')
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase()}
              </div>
            </div>
          )}
          <button
            onClick={() => setLogoutOpen(true)}
            className={`w-full flex items-center rounded-lg text-slate-400 hover:text-white hover:bg-white/10 cursor-pointer transition-all ${
              isSidebarOpen ? 'gap-2 px-3 py-1.5 text-xs' : 'justify-center py-2 text-sm'
            }`}
            title={!isSidebarOpen ? 'Sign out' : undefined}
          >
            <i className="ri-logout-box-line" />
            {isSidebarOpen && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Top bar + content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-[#e2e8f0] px-6 h-[60px] flex items-center justify-between shrink-0">
          <p className="text-sm font-semibold text-[#1e293b]">
            Welcome, {user?.name ?? 'Guard'} &mdash; Security Guard
          </p>
          <p className="text-sm font-semibold text-[#1e293b]">
            <span className="text-slate-400 font-normal mr-2">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              })}
            </span>
          </p>
        </header>
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>

      {/* Logout confirm */}
      {logoutOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setLogoutOpen(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-logout-box-line text-red-600 text-xl" />
            </div>
            <h3 className="text-base font-semibold text-[#1e293b] mb-1">
              Sign Out
            </h3>
            <p className="text-sm text-[#64748b] mb-6">
              Are you sure you want to sign out of the guard portal?
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setLogoutOpen(false)}
                className="flex-1 h-10 rounded-lg text-sm font-semibold bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0] cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setLogoutOpen(false);
                  await logout();
                }}
                className="flex-1 h-10 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 cursor-pointer"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
